import { Component, computed, DestroyRef, effect, EventEmitter, inject, input, Output, PLATFORM_ID, signal } from '@angular/core';
import { MatchGaugeComponent } from "../gauge-meter/gauge-meter";
import { isPlatformBrowser } from '@angular/common';
import { dropdownAnimation } from '../../../../shared/animation/animation';
import { JobDetailService } from '../../services/job-details.service';
import { MatchingRequest, MatchingResponse } from '../../models/details.model';
import { tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LocalstorageService } from '../../../../core/services/essentials/localstorage.service';
import { LocalStorageItem, MatchingStatus } from '../../../../shared/enums/app.enums';
import { MatchingScoreItem } from '../../../apply/interfaces/apply.interface';
import { SharedService } from '../../../jobs/services/shared.service';
import { ProModal } from "../../../../shared/components/pro-modal/pro-modal";
import { TranslocoModule } from '@jsverse/transloco';
import { ToBanglaNumberPipe } from '../../../../shared/pipes/toBanglaNumber.pipe';
import { environment } from '../../../../../environments/environment';
import { AiMatchingLoaderComponent } from '../ai-matching-loader/ai-matching-loader';

@Component({
  selector: 'app-matching-section',
  imports: [MatchGaugeComponent, ProModal, TranslocoModule, ToBanglaNumberPipe, AiMatchingLoaderComponent],
  templateUrl: './matching-section.html',
  styleUrl: './matching-section.scss',
  animations: [dropdownAnimation]
})
export class MatchingSection {
  private jobDetailsService = inject(JobDetailService);
  private sharedService = inject(SharedService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private localStorageService = inject(LocalstorageService);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);

  jobId = input<string>('');
  matchingData = input<MatchingResponse[] | null>(null);
  isMatchingDataReady = input<boolean | null>(null);
  @Output() matchingDataLoaded = new EventEmitter<MatchingResponse[]>();

  isMatchingApiCalled = signal(false);
  showDetails = signal(false);
  isLoading = signal(false);
  isApiDataReady = signal(false);

  MatchingList = signal<MatchingResponse[]>([]);
  showPricingPop = signal(false);
  matchingStatus = MatchingStatus;
  environment = environment;

  readonly effectiveMatchingList = computed(() => {
    const fromParent = this.matchingData();
    if (fromParent && fromParent.length > 0) {
      return fromParent;
    }
    return this.MatchingList();
  });

  readonly effectiveIsMatchingApiCalled = computed(() => {
    return this.isMatchingApiCalled() || (this.isMatchingDataReady() ?? false);
  });

  aiMatchInfo = computed(() => this.effectiveMatchingList().filter(item => item.isAiGenerated));
  matchingInfo = computed(() => this.effectiveMatchingList().filter(item => !item.isAiGenerated));

  protected readonly matchingPercentage = computed(() => {
    return this.effectiveMatchingList()[0]?.applicantMatchingScore ?? 0;
  });

  protected readonly topApplicantMatchingPercentage = computed(() => {
    return this.effectiveMatchingList()[0]?.topApplicantMatchingPercentage ?? 0;
  });

  protected readonly hasMandatoryUnmatched = computed(() => {
    return this.effectiveMatchingList().some(item => {
      if (!item.isMandatory) return false;

      const status = item.status?.toLowerCase();
      const criteria = item.criteriaName?.toLowerCase();
      // Experience + partly matched: only block on specific reason
      if (criteria.toLowerCase() === 'experience' && status.toLowerCase() === 'partly matched') {
        return false;
      }

      return status !== 'matched';
    });
  });

  isProUser = toSignal(this.sharedService.isProuser$, { initialValue: false });

  constructor() {
    effect(() => {
      const ext = this.matchingData();
      if (ext && ext.length > 0) {
        console.log('[MatchingSection] Received matchingData from parent:', ext);
        this.MatchingList.set(ext);
        this.isMatchingApiCalled.set(true);
        this.isApiDataReady.set(true);
      }
    });
  }

  MatchingScoreBreakdown(): void {
    if (this.isProUser() !== true) {
      this.showPricingPop.set(true);
      return;
    }
    if (this.effectiveIsMatchingApiCalled() && this.effectiveMatchingList().length > 0) {
      this.showDetails.set(true);
      return;
    }
    this.isLoading.set(true);
    this.isApiDataReady.set(false);

    const payload: MatchingRequest = {
      GuidId: '',
      JobId: Number(this.jobId())
    };

    this.jobDetailsService.getMatchingInfo(payload)
      .pipe(
        tap((response) => this.updateMatchingScoreStorage(response)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.MatchingList.set(response);
          this.matchingDataLoaded.emit(response);
          this.isApiDataReady.set(true);
        },
        error: (err: unknown) => {
          console.error('[MatchingSection] Error fetching matching breakdown:', err);
          this.isLoading.set(false);
          this.isApiDataReady.set(false);
        }
      });
  }

  onLoaderComplete(): void {
    this.isMatchingApiCalled.set(true);
    this.showDetails.set(true);
    this.isLoading.set(false);
  }

  cancelLoading(): void {
    this.isLoading.set(false);
    this.isApiDataReady.set(false);
  }

  private updateMatchingScoreStorage(matchingData: MatchingResponse[]): void {
    const jobId = this.jobId().toString();
    const score = matchingData?.[0]?.applicantMatchingScore?.toString() ?? '0';

    const matchingScoreStorageArr = this.getMatchingScoreArr();
    const existingIndex = matchingScoreStorageArr.findIndex(
      (item: MatchingScoreItem) => item.jobId === jobId
    );

    if (existingIndex !== -1) {
      matchingScoreStorageArr[existingIndex].matchingScore = score;
    } else {
      matchingScoreStorageArr.push({ jobId, matchingScore: score });
      if (matchingScoreStorageArr.length > 10) {
        matchingScoreStorageArr.shift();
      }
    }

    this.localStorageService.setItem(
      LocalStorageItem.MATCHING_SCORE_ARR,
      JSON.stringify(matchingScoreStorageArr)
    );
  }

  private getMatchingScoreArr(): MatchingScoreItem[] {
    const matchingArr = this.localStorageService.getItem(
      LocalStorageItem.MATCHING_SCORE_ARR
    );

    if (matchingArr && matchingArr.trim().startsWith('[')) {
      const parsed = JSON.parse(matchingArr);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }

    return [];
  }
}
